import { readErrorMessage, readFieldErrors } from '@Components/util/utils'
import { RootState } from '@AppState/redux-store'
import { PlayslipThemeServiceAPI } from '@API/data-services/playslip-theme-service'
import { DeleteConfirmationDialog } from '@Components/common/delete-confirmation-dialog'
import { WithPermissions } from '@Components/common/with-permissions'
import { AuthScopes, PlayslipTheme, ResponseError } from '@Generated/back-office-client/src'
import {
  Badge,
  Button,
  Container,
  Flex,
  Heading,
  Section,
  Skeleton,
  Text,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import {
  CREATE_FAILED_MESSAGE,
  DEFAULT_THEME_ID,
  DELETE_FAILED_MESSAGE,
  LOAD_FAILED_MESSAGE,
  THEME_ID_ERROR,
  THEME_ID_PATTERN,
  THEME_ID_TAKEN_ERROR,
  COLOR_ROLES,
} from './constants'
import { themesQueryKey } from './utils'
import './styles.scss'

export const PlayslipThemeList = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const queryKey = themesQueryKey(activeCustomerId)

  // Null while the add form is closed. The identifier is its only field; colours are the editor's.
  const [newThemeId, setNewThemeId] = useState<string | null>(null)
  const [newThemeError, setNewThemeError] = useState<string | null>(null)
  const [deletingTheme, setDeletingTheme] = useState<PlayslipTheme | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const {
    data: themes,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey,
    refetchOnMount: 'always',
    queryFn: async () => (await PlayslipThemeServiceAPI.getPlayslipThemes()).items,
    // A 4xx answers the same way every time, so retrying only delays the load error.
    retry: (failureCount, error) => {
      if (error instanceof ResponseError && error.response.status < 500) {
        return false
      }

      return failureCount < 1
    },
  })

  const createMutation = useMutation({
    // A theme starts with no colours set; every role falls back until the editor fills them in.
    mutationFn: (themeId: string) =>
      PlayslipThemeServiceAPI.createPlayslipTheme({ themeId, colors: {} }),
    onSuccess: (theme) => {
      queryClient.invalidateQueries({ queryKey })
      setNewThemeId(null)
      navigate(theme.themeId)
    },
    onError: async (error: unknown) => {
      setNewThemeError(await readCreateError(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (themeId: string) => PlayslipThemeServiceAPI.deletePlayslipTheme(themeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      setDeletingTheme(null)
      setDeleteError(null)
    },
    onError: async (error: unknown) => {
      setDeleteError(await readErrorMessage(error, DELETE_FAILED_MESSAGE))
      setDeletingTheme(null)
    },
  })

  /** A new attempt drops the last one's banner, which otherwise outlives the row it named. */
  const startDeleting = (theme: PlayslipTheme) => {
    setDeleteError(null)
    setDeletingTheme(theme)
  }

  const handleCreate = () => {
    const themeId = (newThemeId ?? '').trim()
    setNewThemeError(null)

    if (!THEME_ID_PATTERN.test(themeId)) {
      setNewThemeError(THEME_ID_ERROR)
      return
    }

    createMutation.mutate(themeId)
  }

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="4">
          <Flex justify="between" align="center">
            <Heading size="6">Playslip Themes</Heading>
            <WithPermissions requiredPermissions={AuthScopes.writegames}>
              <Button
                onClick={() => {
                  setNewThemeError(null)
                  setNewThemeId('')
                }}>
                Add theme
              </Button>
            </WithPermissions>
          </Flex>

          {loadError && (
            <Text size="2" color="red" data-testid="playslip-themes-load-error">
              {LOAD_FAILED_MESSAGE}
            </Text>
          )}

          {deleteError && (
            <Text size="2" color="red" data-testid="playslip-theme-delete-error">
              {deleteError}
            </Text>
          )}

          {newThemeId !== null && (
            <Flex direction="column" gap="2" className="playslip-theme-form">
              <label htmlFor="playslip-theme-id">
                <Text size="2" weight="medium">
                  Theme identifier
                </Text>
              </label>
              <TextField.Root
                id="playslip-theme-id"
                placeholder="cowboy-draw"
                value={newThemeId}
                onChange={(e) => setNewThemeId(e.target.value)}
              />
              <Text size="1" color="gray">
                A host app sends this to select the theme. It cannot be changed afterwards.
              </Text>
              {newThemeError && (
                <Text size="1" color="red">
                  {newThemeError}
                </Text>
              )}
              <Flex gap="2">
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating...' : 'Create theme'}
                </Button>
                <Button variant="outline" onClick={() => setNewThemeId(null)}>
                  Cancel
                </Button>
              </Flex>
            </Flex>
          )}

          {isLoading ? (
            <Skeleton loading={true}>
              <Flex direction="column" gap="2" data-testid="playslip-themes-loading-skeleton">
                <div className="playslip-theme-skeleton-card" />
                <div className="playslip-theme-skeleton-card" />
              </Flex>
            </Skeleton>
          ) : themes && themes.length > 0 ? (
            <Flex direction="column" gap="2">
              {themes.map((theme) => (
                <Flex
                  key={theme.themeId}
                  data-testid={`playslip-theme-${theme.themeId}`}
                  className="playslip-theme-row"
                  gap="3"
                  align="center">
                  <Flex direction="column" gap="2" flexGrow="1">
                    <Flex gap="2" align="center">
                      <Link to={theme.themeId}>
                        <Text size="3" weight="medium">
                          {theme.themeId}
                        </Text>
                      </Link>
                      {theme.themeId === DEFAULT_THEME_ID && (
                        <Badge color="green">Used when an app names no theme</Badge>
                      )}
                    </Flex>
                    <Flex gap="1" align="center">
                      {COLOR_ROLES.filter(({ role }) => theme.colors[role]).map(
                        ({ role, label }) => (
                          <span
                            key={role}
                            data-testid={`playslip-theme-swatch-${theme.themeId}-${role}`}
                            className="playslip-theme-swatch"
                            role="img"
                            aria-label={label}
                            style={{ backgroundColor: theme.colors[role] }}
                          />
                        ),
                      )}
                    </Flex>
                  </Flex>
                  <WithPermissions requiredPermissions={AuthScopes.writegames}>
                    <Button variant="outline" color="red" onClick={() => startDeleting(theme)}>
                      Delete
                    </Button>
                  </WithPermissions>
                </Flex>
              ))}
            </Flex>
          ) : (
            // Only when the load succeeded — otherwise a failure reads as a customer with no themes.
            !loadError && (
              <Flex direction="column" align="center" gap="2" py="8">
                <Text size="4" weight="medium">
                  No playslip themes
                </Text>
                <Text size="2" color="gray">
                  Until one is added, playslips use the app&apos;s built-in colours
                </Text>
              </Flex>
            )
          )}
        </Flex>

        <DeleteConfirmationDialog
          open={!!deletingTheme}
          title="Delete Playslip Theme"
          description={`Are you sure you want to delete “${deletingTheme?.themeId}”? An app asking for it will fall back to its built-in colours.`}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeletingTheme(null)}
          onConfirm={() => deletingTheme && deleteMutation.mutate(deletingTheme.themeId)}
        />
      </Section>
    </Container>
  )
}

/**
 * The identifier is the only field the form has, so every failure is reported against it: a
 * conflict is a name already taken, and a validation error names the identifier itself.
 */
const readCreateError = async (error: unknown): Promise<string> => {
  if (error instanceof ResponseError && error.response.status === 409) {
    return THEME_ID_TAKEN_ERROR
  }

  const fieldErrors = await readFieldErrors(error)

  return fieldErrors.themeId ?? (await readErrorMessage(error, CREATE_FAILED_MESSAGE))
}
